// Validation utilities

import { z } from 'zod';

/**
 * Email validation schema
 */
export const emailSchema = z.string().email('Email inválido');

/**
 * Hiring code validation schema (5 characters alphanumeric)
 */
export const hiringCodeSchema = z
  .string()
  .length(5, 'El código debe tener 5 caracteres')
  .regex(/^[A-Z0-9]{5}$/, 'El código debe ser alfanumérico en mayúsculas');

/**
 * Login form schema
 */
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
});

/**
 * Confirm data schema
 */
export const confirmDataSchema = z.object({
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'Debes aceptar los términos y condiciones',
  }),
});

/**
 * Validate hiring code format
 */
export function isValidHiringCode(code: string): boolean {
  try {
    hiringCodeSchema.parse(code);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  try {
    emailSchema.parse(email);
    return true;
  } catch {
    return false;
  }
}

/**
 * Sanitize string by removing dangerous characters
 * Removes null bytes, control characters, and other potentially dangerous characters
 */
export function sanitizeString(value: string): string {
  if (!value || typeof value !== 'string') return value;
  
  return value
    .replace(/\x00/g, '') // Remove null bytes
    .replace(/[\x01-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '') // Remove control characters except \n, \r, \t
    .replace(/\r\n/g, '\n') // Normalize line breaks
    .trim();
}

/**
 * Validate email format strictly (more strict than HTML5 email validation)
 * Rejects emails like test..test@test.com, test@test.c, etc.
 */
export function isValidEmailStrict(email: string): boolean {
  if (!email || typeof email !== 'string') return false;
  
  // Basic email regex (more strict than HTML5)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  
  if (!emailRegex.test(email)) return false;
  
  // Reject emails with consecutive dots
  if (email.includes('..')) return false;
  
  // Reject emails with dot at start or end of local part
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return false;
  if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
  
  // Reject domains with less than 2 characters after last dot
  const domainParts = domain.split('.');
  if (domainParts.length < 2) return false;
  const tld = domainParts[domainParts.length - 1];
  if (tld.length < 2) return false;
  
  return true;
}

/**
 * Check if string contains XSS patterns
 */
export function containsXSS(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  
  const xssPatterns = [
    /<script[^>]*>.*?<\/script>/gi,
    /<img[^>]*onerror[^>]*>/gi,
    /javascript:/gi,
    /<svg[^>]*onload[^>]*>/gi,
    /on\w+\s*=/gi, // onerror=, onclick=, etc.
    /<iframe[^>]*>/gi,
    /<object[^>]*>/gi,
    /<embed[^>]*>/gi,
  ];
  
  return xssPatterns.some(pattern => pattern.test(value));
}

/**
 * Sanitize string to prevent XSS (removes HTML tags and dangerous attributes)
 * Protects against: script tags, event handlers, javascript: URIs, CSS injection,
 * Data URIs, HTML entities, iframes, objects, embeds
 */
export function sanitizeXSS(value: string): string {
  if (!value || typeof value !== 'string') return value;

  // Decode URL encoding first (%3C -> <, etc.)
  try {
    if (value.includes('%')) {
      value = decodeURIComponent(value.replace(/\+/g, ' '));
    }
  } catch {
    // Si decodeURIComponent falla (URI malformado), seguir con el valor original
  }

  // Decode HTML entities first to catch encoded payloads
  let sanitized = value
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n, 10)))
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16)));

  // Remove script tags (including nested/case-mixed)
  sanitized = sanitized.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

  // Remove event handlers (case-insensitive: onerror, OnCLICK, etc.)
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]+/gi, '');

  // Remove javascript:, data:, vbscript: URIs
  sanitized = sanitized.replace(/javascript\s*:/gi, '');
  sanitized = sanitized.replace(/data\s*:\s*text\/html[^"'\s]*/gi, '');
  sanitized = sanitized.replace(/vbscript\s*:/gi, '');

  // Remove CSS injection (style tags, url('javascript:...'), expression())
  sanitized = sanitized.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  sanitized = sanitized.replace(/url\s*\(\s*['"]?\s*javascript\s*:[^)]*\)/gi, '');
  sanitized = sanitized.replace(/expression\s*\([^)]*\)/gi, '');
  sanitized = sanitized.replace(/<link[^>]*href\s*=\s*["']?\s*javascript\s*:[^"'\s>]*/gi, '');

  // Remove dangerous elements
  sanitized = sanitized
    .replace(/<img[^>]*>/gi, (m) => m.replace(/\s*on\w+\s*=\s*[^\s>]+/gi, ''))
    .replace(/<img[^>]*on\w+[^>]*>/gi, '')
    .replace(/<svg[^>]*>[\s\S]*?<\/svg>/gi, (m) => m.replace(/\s*on\w+\s*=\s*[^\s>]+/gi, ''))
    .replace(/<svg[^>]*on\w+[^>]*>/gi, '')
    .replace(/<iframe[^>]*>/gi, '')
    .replace(/<object[^>]*>/gi, '')
    .replace(/<embed[^>]*>/gi, '')
    .replace(/<form[^>]*>/gi, '')
    .replace(/<base[^>]*>/gi, '');

  // Remove any remaining event handler patterns
  sanitized = sanitized.replace(/\s*on\w+\s*=/gi, '');

  return sanitized;
}

/**
 * Validate UUID v4 format (case-insensitive)
 */
export function isValidUUID(value: string | undefined | null): boolean {
  if (!value || typeof value !== 'string') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

/**
 * Validate name: must not be empty after trim, must not exceed maxLength
 */
export function isValidName(name: string, maxLength: number = 255): boolean {
  if (!name || typeof name !== 'string') return false;
  const trimmed = name.trim();
  if (trimmed.length === 0) return false;
  if (trimmed.length > maxLength) return false;
  return true;
}

